/**
 * NotificationToggle - Enable/disable push notifications
 */
import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { subscribeToPush, unsubscribeFromPush, isPushSubscribed, registerServiceWorker } from "../utils/pwa";

const NotificationToggle = ({ compact = false }) => {
  const { token } = useAuth();
  const [subscribed, setSubscribed] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [supported, setSupported] = useState(true);

  useEffect(() => {
    if (!('PushManager' in window) || !('serviceWorker' in navigator)) {
      setSupported(false);
      return;
    }
    registerServiceWorker();
    isPushSubscribed().then(setSubscribed);
  }, []);

  const handleToggle = async () => {
    try {
      setToggling(true);
      if (subscribed) {
        await unsubscribeFromPush(token);
        setSubscribed(false);
        toast.success("Notifications disabled");
      } else {
        const result = await Notification.requestPermission();
        if (result === "granted") {
          const success = await subscribeToPush(token);
          if (success) {
            setSubscribed(true);
            toast.success("Notifications enabled!");
          } else {
            toast.error("Failed to enable notifications");
          }
        } else {
          toast.error("Please allow notifications in your browser settings");
        }
      }
    } catch (error) {
      toast.error("Failed to toggle notifications");
    } finally {
      setToggling(false);
    }
  };

  if (!supported) return null;

  if (compact) {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={handleToggle}
        disabled={toggling}
        className="relative"
        data-testid="notification-toggle-compact"
      >
        {toggling ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : subscribed ? (
          <Bell className="w-4 h-4 text-primary" />
        ) : (
          <BellOff className="w-4 h-4 text-muted-foreground" />
        )}
        {subscribed && (
          <span className="absolute top-0 right-0 w-2 h-2 bg-green-500 rounded-full" />
        )}
      </Button>
    );
  }

  return (
    <div className="flex items-center justify-between p-3 rounded-lg border" data-testid="notification-toggle">
      <div className="flex items-center gap-3">
        {subscribed ? (
          <Bell className="w-5 h-5 text-primary" />
        ) : (
          <BellOff className="w-5 h-5 text-muted-foreground" />
        )}
        <div>
          <p className="text-sm font-medium">Push Notifications</p>
          <p className="text-xs text-muted-foreground">
            {subscribed ? "You'll receive alerts for violations, DRC updates, payments" : "Enable to stay updated on community events"}
          </p>
        </div>
      </div>
      <Button
        variant={subscribed ? "outline" : "default"}
        size="sm"
        onClick={handleToggle}
        disabled={toggling}
        data-testid="notification-toggle-btn"
      >
        {toggling ? <Loader2 className="w-4 h-4 animate-spin" /> : subscribed ? "Disable" : "Enable"}
      </Button>
    </div>
  );
};

export default NotificationToggle;
